
import { useState } from "react";
import { X, Info } from "lucide-react";
import { Button } from "@/components/Button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useCookieConsent, type CookiePreferences } from "@/hooks/useCookieConsent";
import { Link } from "react-router-dom";

export const CookieSettings = () => {
  const { showSettings, preferences, savePreferences, closeSettings } = useCookieConsent();
  const [tempPreferences, setTempPreferences] = useState<CookiePreferences>(preferences);

  const handleSave = () => {
    savePreferences(tempPreferences);
  };

  const handleToggle = (key: keyof CookiePreferences) => {
    if (key === 'essential') return; // Essential cookies can't be disabled
    setTempPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  return (
    <Dialog open={showSettings} onOpenChange={closeSettings}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto bg-background border-white/10">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-white">Cookie Preferences</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 mt-4">
          <p className="text-white/80 text-sm">
            We use cookies to provide you with the best experience on our website. You can choose which types of cookies you want to accept. 
            Your choices will be saved and can be changed at any time.
          </p>

          {/* Essential Cookies */}
          <div className="border border-white/10 rounded-lg p-4 bg-white/5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-white">Essential Cookies</h3>
                <div className="bg-green-500/20 text-green-400 px-2 py-1 rounded text-xs">Required</div>
              </div>
              <Switch checked={true} disabled />
            </div>
            <p className="text-sm text-white/70">
              These cookies are necessary for the website to function and cannot be switched off. They are usually only set in response to actions made by you.
            </p>
          </div>

          {/* Analytics Cookies */}
          <div className="border border-white/10 rounded-lg p-4 bg-white/5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-white">Analytics Cookies</h3>
              <Switch 
                checked={tempPreferences.analytics} 
                onCheckedChange={() => handleToggle('analytics')}
              />
            </div>
            <p className="text-sm text-white/70">
              These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. This helps us improve our website performance.
            </p>
          </div>

          {/* Marketing Cookies */}
          <div className="border border-white/10 rounded-lg p-4 bg-white/5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-white">Marketing Cookies</h3>
              <Switch 
                checked={tempPreferences.marketing} 
                onCheckedChange={() => handleToggle('marketing')}
              />
            </div>
            <p className="text-sm text-white/70">
              These cookies are used to deliver advertisements more relevant to you and your interests. They may also be used to limit the number of times you see an advertisement.
            </p>
          </div>

          {/* CCPA Compliance */}
          <div className="border border-white/10 rounded-lg p-4 bg-white/5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-white">Do Not Sell My Personal Information</h3>
                <Info className="h-4 w-4 text-white/50" />
              </div>
              <Switch 
                checked={tempPreferences.ccpaOptOut} 
                onCheckedChange={() => handleToggle('ccpaOptOut')}
              />
            </div>
            <p className="text-sm text-white/70">
              Under the California Consumer Privacy Act (CCPA), you have the right to opt-out of the sale of your personal information. 
              Enabling this will prevent us from sharing your data with third parties for marketing purposes.
            </p>
          </div>

          <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-blue-400 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-medium text-blue-300 mb-1">Your Privacy Rights</h4>
                <p className="text-sm text-blue-200/80">
                  You can change your cookie preferences at any time by clicking the "Cookie Settings" link in our footer. 
                  For more information about how we handle your data, please read our{" "}
                  <Link to="/privacy-policy" className="text-blue-300 hover:text-blue-200 underline">
                    Privacy Policy
                  </Link>.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mt-6">
          <Button
            variant="outline"
            onClick={closeSettings}
            className="border-white/20 text-white hover:bg-white/10 flex-1"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            className="flex-1"
          >
            Save Preferences
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
